import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import Table from '../../../../components/tables/Table';
import tableStyles from '../../../../components/tables/Table.styles';
import styles from './MeterDetailsTable.styles';
import { monthNameUTC, isSameUtcMonth, isIsoEqual } from '../../utils/meterDetails.helpers';

export default function MeterDetailsTable({
  files = [],
  viewDate = new Date(),
  setViewDate = () => {},
  selectedDate = null,
  setSelectedDate = () => {},
}) {
  const safeFiles = useMemo(() => Array.isArray(files) ? files : [], [files]);

  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => { setRefreshKey((k) => k + 1); }, [viewDate, selectedDate]);

  const prevMonth = () => {
    setViewDate((d) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - 1, 1)));
    setSelectedDate(null);
  };
  const nextMonth = () => {
    setViewDate((d) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1)));
    setSelectedDate(null);
  };

  const monthName = monthNameUTC(viewDate);

  const filteredFiles = useMemo(() => {
    if (selectedDate) {
      return safeFiles.filter(
        (f) => f?.ReadingDate && isIsoEqual(f.ReadingDate, selectedDate)
      );
    }
    return safeFiles.filter(
      (f) => f?.ReadingDate && isSameUtcMonth(f.ReadingDate, viewDate)
    );
  }, [safeFiles, selectedDate, viewDate]);

  const COLUMNS = [
    {
      label: 'Reading Date',
      field: 'ReadingDate',
      type: 'date',
      sortable: true,
      render: (_row, v) => (v ? new Date(v).toLocaleDateString('en-GB') : '—'),
    },
    {
      label: 'File Name',
      field: 'filename',
      type: 'string',
      sortable: true,
      render: (_row, v) => v || '—',
    },
    {
      label: 'Loaded Date',
      field: 'Loaded Date',
      type: 'date',
      sortable: true,
      accessor: (row) => row?.['Loaded Date'] || '',
      render: (_row, v) => (v ? new Date(v).toLocaleString('en-GB') : '—'),
    },
    {
      label: 'Status',
      field: 'Status',
      type: 'string',
      sortable: true,
      render: (_row, v) => v || '—',
    },
  ];

  const emptyNode = (
    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.88rem' }}>
      {selectedDate
        ? 'No meter data uploaded for this day'
        : 'No meter data uploaded for this month'}
    </Typography>
  );

  return (
    <Box sx={{ ...tableStyles.tableWrapper, minHeight: 300 }}>
      <Box sx={{ ...styles.calendarHeaderBar }}>
        <Typography sx={{ fontWeight: 600 }}>Meter Data Status</Typography>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
          <IconButton size="small" aria-label="Previous month" onClick={prevMonth}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle2" sx={{ ...styles.calendarMonthLabel, mx: 1 }}>
            {monthName}
          </Typography>
          <IconButton size="small" aria-label="Next month" onClick={nextMonth}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Table
        key={refreshKey}
        rows={filteredFiles}
        columns={COLUMNS}
        stickyHeader
        zebra
        hover
        bordered
        emptyMessage={emptyNode} 
      />
    </Box>
  );
}
